import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { suppliersApi, type SupplierRequest, type SupplierView } from "../../api/assets";
import type { ApiError } from "../../api/client";
import { detailsMap, formatApiError } from "../../lib/apiError";
import { refreshSuppliers } from "../../lib/useSuppliers";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { HistoryPanel } from "../../components/HistoryPanel";

type Form = {
  name: string;
  taxId: string;
  registrationNo: string;
  address: string;
  city: string;
  phone: string;
  email: string;
  contactPerson: string;
  note: string;
};

const EMPTY: Form = { name: "", taxId: "", registrationNo: "", address: "", city: "", phone: "", email: "", contactPerson: "", note: "" };

function formOf(s: SupplierView): Form {
  return {
    name: s.name,
    taxId: s.taxId ?? "",
    registrationNo: s.registrationNo ?? "",
    address: s.address ?? "",
    city: s.city ?? "",
    phone: s.phone ?? "",
    email: s.email ?? "",
    contactPerson: s.contactPerson ?? "",
    note: s.note ?? "",
  };
}

const TAX_ID_RE = /^\d{9}$/;
const REGISTRATION_NO_RE = /^\d{8}$/;

/**
 * `/admin/dobavljaci/novi` and `/admin/dobavljaci/:id` — the supplier's card
 * (sprint 5h): company data in one form and, below it, the history of every
 * change (who, when, which field, old → new value), read from the audit log
 * and refreshed right after a save. Only the name is mandatory. Without
 * `suppliers:edit` the card is read-only.
 */
export default function AdminSupplierEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  const canManage = isNew ? hasPermission("suppliers:create") : hasPermission("suppliers:edit");
  const canDelete = hasPermission("suppliers:delete");

  const [detail, setDetail] = useState<SupplierView | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** Bumped after every save so the history panel fetches the new entry. */
  const [historyKey, setHistoryKey] = useState(0);

  const load = useCallback(async (supplierId: string) => {
    setLoadError(null);
    try {
      const s = await suppliersApi.get(supplierId);
      setDetail(s);
      setForm(formOf(s));
    } catch (err) {
      setLoadError((err as ApiError).message);
    }
  }, []);

  useEffect(() => {
    if (id) void load(id);
    else { setDetail(null); setForm(EMPTY); }
  }, [id, load]);

  function set<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const taxId = form.taxId.trim();
    const registrationNo = form.registrationNo.trim();
    if (taxId && !TAX_ID_RE.test(taxId)) {
      setError(t("suppliers.error.taxId", "PIB ima tačno 9 cifara."));
      return;
    }
    if (registrationNo && !REGISTRATION_NO_RE.test(registrationNo)) {
      setError(t("suppliers.error.registrationNo", "Matični broj ima tačno 8 cifara."));
      return;
    }
    const body: SupplierRequest = {
      name: form.name.trim(),
      taxId: taxId || null,
      registrationNo: registrationNo || null,
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      contactPerson: form.contactPerson.trim() || null,
      note: form.note.trim() || null,
    };
    setSubmitting(true);
    try {
      if (isNew) {
        const created = await suppliersApi.create(body);
        await refreshSuppliers();
        toast.success(t("suppliers.toast.created", "Dobavljač dodat."));
        navigate(`/admin/dobavljaci/${created.id}`, { replace: true });
      } else if (id) {
        const saved = await suppliersApi.update(id, body);
        setDetail(saved);
        setForm(formOf(saved));
        setHistoryKey((k) => k + 1);
        await refreshSuppliers();
        toast.success(t("suppliers.toast.saved", "Dobavljač sačuvan."));
      }
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "NAME_TAKEN") {
        setError(t("suppliers.error.nameTaken", "Dobavljač „{{name}}“ već postoji.", { name: body.name }));
      } else if (ae.code === "TAX_ID_TAKEN") {
        setError(t("suppliers.error.taxIdTaken", "Ovaj PIB već ima dobavljač „{{name}}“.", { name: String(detailsMap(ae)?.supplierName ?? "") }));
      } else if (ae.code === "VALIDATION" && detailsMap(ae)?.email) {
        setError(t("suppliers.error.email", "E-mail adresa nije ispravna."));
      } else {
        setError(formatApiError(ae));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!detail) return;
    const ok = await confirm({
      title: t("suppliers.confirmDelete.title", "Brisanje dobavljača"),
      message: t("suppliers.confirmDelete.message", "Obrisati dobavljača „{{name}}“ iz šifarnika?", { name: detail.name }),
      confirmLabel: t("action.delete", "Obriši"),
      danger: true,
    });
    if (!ok) return;
    try {
      await suppliersApi.remove(detail.id);
      await refreshSuppliers();
      toast.success(t("suppliers.toast.deleted", "Dobavljač obrisan."));
      navigate("/admin/dobavljaci", { replace: true });
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "IN_USE") {
        const count = Number(detailsMap(ae)?.assetCount ?? detail.assetCount);
        toast.error(t("suppliers.error.inUse", "Dobavljač je upisan na {{count}} asseta — prvo ga promenite na tim assetima.", { count }));
      } else {
        toast.error(formatApiError(ae));
      }
    }
  }

  const readOnly = !canManage;
  const title = isNew ? t("suppliers.editor.newTitle", "Novi dobavljač") : detail?.name ?? t("suppliers.editor.title", "Dobavljač");

  return (
    <div>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label="Breadcrumb">
            <span><Link to="/admin/dobavljaci">{t("suppliers.title", "Dobavljači")}</Link></span>
            <span>{isNew ? t("suppliers.editor.newCrumb", "Novi") : detail?.name ?? "…"}</span>
          </nav>
          <h1>{title}</h1>
          {!isNew && detail && (
            <div className="sub">
              {detail.assetCount > 0
                ? t("suppliers.editor.assetCount", "Upisan na {{count}} asseta", { count: detail.assetCount })
                : t("suppliers.editor.noAssets", "Nije upisan ni na jednom assetu")}
            </div>
          )}
        </div>
        {canDelete && !isNew && detail && (
          <div className="page-actions">
            <button type="button" className="btn" style={dangerBtn} onClick={handleDelete}>{t("suppliers.delete", "Obriši dobavljača")}</button>
          </div>
        )}
      </div>

      {loadError && <div style={errorBox} role="alert">{loadError}</div>}
      {!isNew && !detail && !loadError && <div className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></div>}

      {(isNew || detail) && (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <section className="card">
            <div className="card-h"><h3>{t("suppliers.editor.section", "Podaci dobavljača")}</h3></div>
            <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label className="field">
                <span>{t("suppliers.field.name", "Naziv")} <span className="req-mark">*</span></span>
                <input className="input" value={form.name} onChange={(e) => set("name", e.target.value)} required maxLength={200} disabled={readOnly} placeholder={t("suppliers.field.namePlaceholder", "npr. Elektro-Remont d.o.o.")} />
              </label>
              <div className="form-grid">
                <label className="field">
                  <span>{t("suppliers.field.taxId", "PIB")}</span>
                  <input className="input" value={form.taxId} onChange={(e) => set("taxId", e.target.value)} maxLength={9} inputMode="numeric" disabled={readOnly} placeholder="101234567" style={{ fontFamily: "var(--mono)" }} />
                </label>
                <label className="field">
                  <span>{t("suppliers.field.registrationNo", "Matični broj")}</span>
                  <input className="input" value={form.registrationNo} onChange={(e) => set("registrationNo", e.target.value)} maxLength={8} inputMode="numeric" disabled={readOnly} placeholder="20123456" style={{ fontFamily: "var(--mono)" }} />
                </label>
              </div>
              <div className="form-grid">
                <label className="field">
                  <span>{t("suppliers.field.address", "Adresa")}</span>
                  <input className="input" value={form.address} onChange={(e) => set("address", e.target.value)} maxLength={300} disabled={readOnly} placeholder={t("suppliers.field.addressPlaceholder", "ulica i broj")} />
                </label>
                <label className="field">
                  <span>{t("suppliers.field.city", "Mesto")}</span>
                  <input className="input" value={form.city} onChange={(e) => set("city", e.target.value)} maxLength={120} disabled={readOnly} />
                </label>
              </div>
              <div className="form-grid">
                <label className="field">
                  <span>{t("suppliers.field.phone", "Telefon")}</span>
                  <input className="input" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} maxLength={60} inputMode="tel" disabled={readOnly} />
                </label>
                <label className="field">
                  <span>{t("suppliers.field.email", "E-mail")}</span>
                  <input className="input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} maxLength={200} inputMode="email" autoCapitalize="off" disabled={readOnly} />
                </label>
              </div>
              <label className="field">
                <span>{t("suppliers.field.contactPerson", "Kontakt osoba")}</span>
                <input className="input" value={form.contactPerson} onChange={(e) => set("contactPerson", e.target.value)} maxLength={200} disabled={readOnly} />
              </label>
              <label className="field">
                <span>{t("suppliers.field.note", "Napomena")}</span>
                <textarea className="input" rows={3} value={form.note} onChange={(e) => set("note", e.target.value)} maxLength={2000} disabled={readOnly} style={textarea} />
              </label>
            </div>
          </section>

          {error && <div style={errorBox} role="alert">{error}</div>}

          {canManage && (
            <div className="form-actions">
              <button type="button" className="btn" onClick={() => navigate("/admin/dobavljaci")}>{isNew ? t("action.cancel", "Otkaži") : t("action.back", "Nazad")}</button>
              <button type="submit" className="btn primary" disabled={submitting || !form.name.trim()}>
                {submitting ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
              </button>
            </div>
          )}
        </form>
      )}

      {/* The reason this is a card and not a modal: what changed on the supplier, and who changed it. */}
      {!isNew && detail && (
        <div style={{ marginTop: 16 }}>
          <HistoryPanel entityKind="SUPPLIER" entityId={detail.id} title={t("suppliers.editor.history", "Istorija izmena")} refreshKey={historyKey} />
        </div>
      )}
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
